import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Input } from 'antd';
import { ApplicationState } from '../../../../reducers';
import { AccountConfigurationEBP } from '../../../../../types/account_configurations';
import { typingDone } from '../../../../utils/Utils';
import { activeBrands } from '../../../../selectors/brand/brandSelector';
import { AsyncDispatch } from '../../../../../types/global';
import { useAppDispatch } from '../../../../store/ReduxStore';
import { fetchAccountEBPs } from '../../../../actions/parent/accountConfigurations/fetch';
import AntMultiSelect from '../../../global/AntMultiSelect';
import AccountEBPsTable from './AccountEBPsTable';

const AccountEBPsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const [searchKeywords, setSearchKeywords] = React.useState<string>('');
  const [filteredData, setFilteredData] = React.useState<AccountConfigurationEBP[]>([]);
  const [selectedCustomFieldIds, setSelectedCustomFieldIds] = React.useState<number[] | undefined>(
    undefined
  );

  const [selectedEBPIds, setSelectedEBPIds] = React.useState<number[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = React.useState<number[]>([]);
  const [sortColumn, setSortColumn] = React.useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = React.useState<string | undefined>(undefined);

  const brands = useSelector((state: ApplicationState) => activeBrands(state));
  const { ebps, isLoading, customFields } = useSelector((state: ApplicationState) => {
    return {
      ebps: state.parent.configurations.ebps.data,
      isLoading: state.parent.configurations.ebps.isLoading,
      customFields: state.parent.parentCustomFields.customFields,
    };
  }, shallowEqual);

  const filterListByColumn = React.useCallback(
    (data: AccountConfigurationEBP[]) => {
      let filterData = [...data];

      if (selectedEBPIds.length > 0) {
        const selectedNames = ebps
          .filter(ebp => selectedEBPIds.includes(ebp.id))
          .map(ebp => ebp.name);
        filterData = filterData.filter(ebp => selectedNames.includes(ebp.name));
      }

      if (selectedBrandIds.length > 0) {
        filterData = filterData.filter(
          ebp => ebp.parent_owner_brand_id && selectedBrandIds.includes(ebp.parent_owner_brand_id)
        );
      }

      return filterData;
    },
    [selectedEBPIds, selectedBrandIds, ebps]
  );

  const sortList = React.useCallback(
    (data: AccountConfigurationEBP[]) => {
      if (!sortColumn || !sortOrder) {
        return data;
      }

      const sortedData = [...data];
      sortedData.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        if (sortColumn === 'name') {
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
        } else if (sortColumn === 'parent_owner_brand_id') {
          const aBrand = brands.find(brand => brand.id === a.parent_owner_brand_id);
          const bBrand = brands.find(brand => brand.id === b.parent_owner_brand_id);
          aValue = aBrand?.name?.toLowerCase() || '';
          bValue = bBrand?.name?.toLowerCase() || '';
        }

        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      });

      return sortedData;
    },
    [sortColumn, sortOrder, brands]
  );

  const handleFilterSort = React.useCallback(() => {
    let filteredEBPs = [...ebps];

    if (searchKeywords) {
      const lowerKeywords = searchKeywords.toLowerCase();
      filteredEBPs = filteredEBPs.filter((ebp: AccountConfigurationEBP) =>
        ebp.name.toLowerCase().includes(lowerKeywords)
      );
    }

    filteredEBPs = filterListByColumn(filteredEBPs);
    const sortedData = sortList(filteredEBPs);
    setFilteredData(sortedData);
  }, [ebps, searchKeywords, filterListByColumn, sortList]);

  React.useEffect(() => {
    if (searchKeywords) {
      typingDone(handleFilterSort);
    } else {
      handleFilterSort();
    }
  }, [handleFilterSort, searchKeywords]);

  const nameListValues = React.useMemo(() => {
    const uniqueNames = new Map<string, number>();
    ebps.forEach(e => {
      if (!uniqueNames.has(e.name)) {
        uniqueNames.set(e.name, e.id);
      }
    });
    return Array.from(uniqueNames, ([name, id]) => ({ id, name }));
  }, [ebps]);
  const brandListValues = React.useMemo(
    () => brands.map(b => ({ id: b.id, name: b.name })),
    [brands]
  );

  const handleCustomFieldsFilter = (value: number[]) => {
    const ids = value.length > 0 ? value : undefined;
    setSelectedCustomFieldIds(ids);
    dispatch(fetchAccountEBPs(ids));
  };

  const handleSort = (column: string, order: string) => {
    setSortColumn(column);
    setSortOrder(order);
  };

  return (
    <div className="page-layout">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <Input
            className="max-w-xl mr-2"
            value={searchKeywords}
            onChange={e => setSearchKeywords(e.target.value)}
            placeholder={t('configurations:searchEBPs')}
            allowClear
          />
          <AntMultiSelect
            className="max-w-md"
            selectedElements={selectedCustomFieldIds || []}
            onChange={handleCustomFieldsFilter}
            placeholder={t('configurations:filterByCustomFields')}
            allowClear
            style={{ minWidth: '300px' }}
            elements={customFields.map(field => ({
              id: field.id,
              name: field.name,
            }))}
            showSearch
          />
        </div>
      </div>
      <div className="page-layout__content flex w-full">
        {isLoading && filteredData.length === 0 ? (
          <div className="loader" />
        ) : (
          <AccountEBPsTable
            filteredData={filteredData}
            isLoading={isLoading}
            brands={brands}
            selectedEBPIds={selectedEBPIds}
            setSelectedEBPIds={setSelectedEBPIds}
            selectedBrandIds={selectedBrandIds}
            setSelectedBrandIds={setSelectedBrandIds}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            nameListValues={nameListValues}
            brandListValues={brandListValues}
          />
        )}
      </div>
    </div>
  );
};

export default AccountEBPsPage;
